/**
 * Pure half of the music-server sign-in isolation: no store, no axios, no window.
 *
 * Every request to Navidrome, Subsonic or Jellyfin carries the sign-in it was
 * sent with (`AuthSessionRef`: server id plus the credential it used). A 401, a
 * Subsonic auth error or a renewed Navidrome token is only applied while that
 * sign-in is still the one stored for the server. An answer of an earlier
 * sign-in (the user signed in again, switched server or logged out meanwhile)
 * is ignored, so it can neither log out nor overwrite the token of the next one.
 *
 * Kept free of `/@/` imports so `tests/auth-session.test.mts` can drive it
 * without a bundler.
 */

/** The sign-in a request was sent with. */
export interface AuthSessionRef {
    /** Subsonic or Jellyfin credential the request carried, if it is the one that matters. */
    credential?: string;
    /** Navidrome JWT (`x-nd-authorization`) the request carried, if it is the one that matters. */
    ndCredential?: string;
    serverId: string;
}

/** The parts of a stored server entry a sign-in is compared against. */
export interface AuthSessionServer {
    credential?: null | string;
    id: string;
    ndCredential?: null | string;
}

/**
 * Is `ref` still the sign-in stored in `server`? Only the credentials the
 * request actually carried are compared; a request that carried none (a login
 * attempt, a probe of an URL) never matches, so its failure cannot end anyone's
 * session.
 */
export const isAuthSessionCurrent = (
    ref: AuthSessionRef | null | undefined,
    server: AuthSessionServer | null | undefined,
): boolean => {
    if (!ref || !server || !ref.serverId || server.id !== ref.serverId) return false;

    const checksCredential = 'credential' in ref;
    const checksNdCredential = 'ndCredential' in ref;
    if (!checksCredential && !checksNdCredential) return false;

    if (checksCredential && (!ref.credential || server.credential !== ref.credential)) {
        return false;
    }

    if (checksNdCredential && (!ref.ndCredential || server.ndCredential !== ref.ndCredential)) {
        return false;
    }

    return true;
};

/**
 * The Navidrome token to store after a response carried `x-nd-authorization`,
 * or null when it must be ignored: no header, a request without a sign-in, or
 * a sign-in that is no longer the stored one (a late answer of an earlier
 * login must not replace the token of the current one).
 */
export const acceptedTokenRenewal = (
    ref: AuthSessionRef | null | undefined,
    server: AuthSessionServer | null | undefined,
    header: unknown,
): null | string => {
    if (typeof header !== 'string' || !header) return null;
    if (!ref || !('ndCredential' in ref)) return null;
    if (!isAuthSessionCurrent(ref, server)) return null;
    if (server?.ndCredential === header) return null;
    return header;
};

/** `setTimeout`/`clearTimeout`, injectable so tests can drive the clock. */
export interface AuthTimers {
    clearTimeout: (handle: unknown) => void;
    setTimeout: (callback: () => void, ms: number) => unknown;
}

const realTimers: AuthTimers = {
    clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
};

export interface AuthFailureScheduler {
    /** Drop any pending failure. */
    cancel: () => void;
    /** Drop the pending failure if it belongs to `ref` (that sign-in just proved to work). */
    cancelFor: (ref: AuthSessionRef | null | undefined) => void;
    /** Drop the pending failure if its sign-in is no longer the stored one. */
    cancelIfStale: () => void;
    /** The sign-in a failure is pending for, if any. */
    pending: () => AuthSessionRef | null;
    /** A request of `ref` was rejected: end that sign-in after `delayMs`, if it is still current then. */
    schedule: (ref: AuthSessionRef) => void;
}

export interface AuthFailureSchedulerDeps {
    delayMs: number;
    /** End this sign-in (toast, logout). Only called while `isCurrent(ref)` holds. */
    fail: (ref: AuthSessionRef) => void;
    /** Read the stored sign-in again at decision time, never from the snapshot of the request. */
    isCurrent: (ref: AuthSessionRef) => boolean;
    timers?: AuthTimers;
}

const sameSession = (a: AuthSessionRef, b: AuthSessionRef) =>
    a.serverId === b.serverId &&
    a.credential === b.credential &&
    a.ndCredential === b.ndCredential &&
    'credential' in a === 'credential' in b &&
    'ndCredential' in a === 'ndCredential' in b;

/**
 * Debounces authentication failures like the old `debounce(authenticationFailure)`
 * did (a burst of 401s ends the session once), but bound to the sign-in that
 * failed: a success of that sign-in cancels it, and when the timer fires the
 * stored sign-in is checked again, so a failure of an earlier login never ends
 * a newer one.
 */
export const createAuthFailureScheduler = (
    deps: AuthFailureSchedulerDeps,
): AuthFailureScheduler => {
    const timers = deps.timers ?? realTimers;
    let pendingRef: AuthSessionRef | null = null;
    let handle: unknown = null;

    const clear = () => {
        if (handle !== null) timers.clearTimeout(handle);
        handle = null;
        pendingRef = null;
    };

    const fire = () => {
        const ref = pendingRef;
        handle = null;
        pendingRef = null;
        if (ref && deps.isCurrent(ref)) deps.fail(ref);
    };

    return {
        cancel: clear,
        cancelFor: (ref) => {
            if (ref && pendingRef && sameSession(pendingRef, ref)) clear();
        },
        cancelIfStale: () => {
            if (pendingRef && !deps.isCurrent(pendingRef)) clear();
        },
        pending: () => pendingRef,
        schedule: (ref) => {
            if (!deps.isCurrent(ref)) return;
            if (handle !== null) timers.clearTimeout(handle);
            pendingRef = ref;
            handle = timers.setTimeout(fire, deps.delayMs);
        },
    };
};

export interface SharedRefresh<T> {
    /** The refresh in flight for `key`, if any. */
    pending: (key: string) => Promise<T> | undefined;
    /** Start the refresh for `key`, or join the one already in flight. */
    run: (key: string, start: () => Promise<T>) => Promise<T>;
}

/**
 * One refresh per key at a time; concurrent callers share its promise and it
 * is forgotten as soon as it settles. Replaces the old recursive
 * `waitForResult` polling, which kept scheduling timers after it resolved.
 */
export const createSharedRefresh = <T>(): SharedRefresh<T> => {
    const inFlight = new Map<string, Promise<T>>();

    return {
        pending: (key) => inFlight.get(key),
        run: (key, start) => {
            const existing = inFlight.get(key);
            if (existing) return existing;

            const promise = Promise.resolve().then(start);
            inFlight.set(key, promise);
            void promise
                .catch(() => undefined)
                .finally(() => {
                    if (inFlight.get(key) === promise) inFlight.delete(key);
                });
            return promise;
        },
    };
};

/** Key of the Navidrome token refresh of one sign-in. */
export const ndRefreshKey = (ref: AuthSessionRef): string =>
    `${ref.serverId}\n${ref.ndCredential ?? ''}`;
